import CONFIG from '../../config';

export interface ABTestConfig {
  id: string;
  variants: Record<string, number>;
}

export interface ABTestAnalytics {
  event: string;
  param: string;
}

export interface ABTestEntry extends ABTestConfig {
  analytics: ABTestAnalytics;
}

interface AbCookie {
  name: string;
  value: string;
}

export const COOKIE_PREFIX = 'ab_';
export const AB_COOKIE_MAX_AGE = 30 * 24 * 60 * 60;

export function pickVariantByWeight(variants: Record<string, number>): string {
  const variantEntries = Object.entries(variants);
  const totalWeight = variantEntries.reduce(
    (sum, [, weight]) => sum + weight,
    0
  );
  let random = Math.random() * totalWeight;

  const picked = variantEntries.find(([, weight]) => {
    random -= weight;
    return random <= 0;
  });

  return picked?.[0] ?? variantEntries[variantEntries.length - 1][0];
}

export function getNewAbCookies(
  getCookie: (name: string) => string | undefined,
  tests: readonly ABTestConfig[]
): AbCookie[] {
  return tests
    .filter(test => {
      const existing = getCookie(`${COOKIE_PREFIX}${test.id}`);
      return !existing || !(existing in test.variants);
    })
    .map(test => ({
      name: `${COOKIE_PREFIX}${test.id}`,
      value: pickVariantByWeight(test.variants)
    }));
}

export function injectCookiesIntoHeaders(
  original: Headers,
  cookies: AbCookie[]
): Headers {
  if (cookies.length === 0) return original;

  const headers = new Headers(original);
  let cookieHeader = headers.get('cookie') || '';

  cookies.forEach(({ name, value }) => {
    cookieHeader += `${cookieHeader ? '; ' : ''}${name}=${value}`;
  });
  headers.set('cookie', cookieHeader);

  return headers;
}

export function buildAbRequestHeaders(
  original: Headers,
  newCookies: AbCookie[]
): Headers {
  const headers = injectCookiesIntoHeaders(original, newCookies);

  headers.set(
    'x-ab-cookies',
    newCookies.map(({ name, value }) => `${name}=${value}`).join('; ')
  );

  return headers;
}

export function parseABCookies(cookieString: string): Record<string, string> {
  const result: Record<string, string> = {};

  if (!cookieString) return result;

  cookieString.split(';').forEach(pair => {
    const [rawName, ...rest] = pair.split('=');
    const name = rawName.trim();
    const value = rest.join('=').trim();

    if (name.startsWith(COOKIE_PREFIX) && value) {
      const testId = name.slice(COOKIE_PREFIX.length);
      result[testId] = value;
    }
  });

  return result;
}

export function getABTests(ctx?: { req?: { headers?: { cookie?: string } } }) {
  const cookieString =
    typeof window === 'undefined'
      ? ctx?.req?.headers?.cookie || ''
      : document.cookie;

  return parseABCookies(cookieString);
}

export function getAbCookieDomain(): string | undefined {
  if (process.env.NODE_ENV === 'development' || !process.env.BINAAZ_HOST) {
    return undefined;
  }

  return `.${CONFIG.host}`;
}

export function getOrCreateABTests(
  ctx: {
    req?: {
      headers?: { cookie?: string; 'x-ab-cookies'?: string };
    };
  },
  tests: readonly ABTestConfig[]
): Record<string, string> {
  const cookieString =
    typeof window === 'undefined'
      ? [ctx?.req?.headers?.cookie, ctx?.req?.headers?.['x-ab-cookies']]
          .filter(Boolean)
          .join('; ')
      : document.cookie;

  const existing = parseABCookies(cookieString);

  const { allVariants, newCookies } = tests.reduce(
    (acc, test) => {
      const valid =
        test.id in acc.allVariants && acc.allVariants[test.id] in test.variants;

      if (valid) return acc;

      const value = pickVariantByWeight(test.variants);

      return {
        allVariants: { ...acc.allVariants, [test.id]: value },
        newCookies: [
          ...acc.newCookies,
          { name: `${COOKIE_PREFIX}${test.id}`, value }
        ]
      };
    },
    { allVariants: existing, newCookies: [] as AbCookie[] }
  );

  if (newCookies.length > 0 && typeof window !== 'undefined') {
    const domain = getAbCookieDomain();
    const domainAttr = domain ? `; Domain=${domain}` : '';

    newCookies.forEach(({ name, value }) => {
      document.cookie = `${name}=${value}; path=/; max-age=${AB_COOKIE_MAX_AGE}${domainAttr}`;
    });
  }

  return allVariants;
}
